import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const CACHE_BUST_QUERY_PARAM = "t";
const CACHE_BUST_INTERVAL_MS = 5 * 60 * 1000;
const CACHE_BUST_VALUE_LENGTH = 6;
const CACHE_BUST_DIGITS = "0123456789";
const IDLE_RELOAD_TIMEOUT_MS = 60 * 60 * 1000;
const IDLE_RELOAD_CHECK_INTERVAL_MS = 30 * 1000;
const USER_ACTIVITY_EVENTS = [
  "click",
  "keydown",
  "mousedown",
  "mousemove",
  "scroll",
  "touchstart",
  "wheel",
] as const;

const createCacheBustValue = () =>
  Array.from(
    { length: CACHE_BUST_VALUE_LENGTH },
    () =>
      CACHE_BUST_DIGITS[Math.floor(Math.random() * CACHE_BUST_DIGITS.length)]
  ).join("");

const createNextCacheBustValue = (currentValue: string | null) => {
  let nextValue = createCacheBustValue();

  while (nextValue === currentValue) {
    nextValue = createCacheBustValue();
  }

  return nextValue;
};

const isPageFocused = () =>
  document.visibilityState === "visible" && document.hasFocus();

export const useRouteCacheBust = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationRef = useRef(location);
  const cacheBustValueRef = useRef<string | null>(null);
  const lastAppliedUrlRef = useRef<string | null>(null);

  const applyCacheBust = useCallback(
    (refreshValue = false) => {
      const latestLocation = locationRef.current;
      const currentUrl = `${latestLocation.pathname}${latestLocation.search}${latestLocation.hash}`;

      if (lastAppliedUrlRef.current === currentUrl) {
        lastAppliedUrlRef.current = null;
        return;
      }

      const searchParams = new URLSearchParams(latestLocation.search);
      const currentCacheBustValue = searchParams.get(CACHE_BUST_QUERY_PARAM);

      if (!cacheBustValueRef.current) {
        cacheBustValueRef.current = createNextCacheBustValue(
          currentCacheBustValue
        );
      }

      if (refreshValue) {
        cacheBustValueRef.current = createNextCacheBustValue(
          currentCacheBustValue
        );
      }

      const nextCacheBustValue = cacheBustValueRef.current;

      if (!refreshValue && currentCacheBustValue === nextCacheBustValue) {
        return;
      }

      searchParams.set(CACHE_BUST_QUERY_PARAM, nextCacheBustValue);

      const nextSearch = `?${searchParams.toString()}`;
      const nextUrl = `${latestLocation.pathname}${nextSearch}${latestLocation.hash}`;

      if (currentUrl === nextUrl) {
        return;
      }

      lastAppliedUrlRef.current = nextUrl;
      navigate(
        {
          pathname: latestLocation.pathname,
          search: nextSearch,
          hash: latestLocation.hash,
        },
        {
          replace: true,
          state: latestLocation.state,
        }
      );
    },
    [navigate]
  );

  useLayoutEffect(() => {
    locationRef.current = location;
    applyCacheBust();
  }, [applyCacheBust, location]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      applyCacheBust(true);
    }, CACHE_BUST_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [applyCacheBust]);

  useEffect(() => {
    let lastInteractionAt = Date.now();
    let waitingStartedAt = isPageFocused() ? null : Date.now();

    const reloadPage = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const currentCacheBustValue = searchParams.get(CACHE_BUST_QUERY_PARAM);
      const nextCacheBustValue = createNextCacheBustValue(
        currentCacheBustValue
      );

      searchParams.set(CACHE_BUST_QUERY_PARAM, nextCacheBustValue);

      window.location.replace(
        `${window.location.pathname}?${searchParams.toString()}${
          window.location.hash
        }`
      );
    };

    const reloadIfExpired = () => {
      const now = Date.now();
      const isIdleExpired = now - lastInteractionAt >= IDLE_RELOAD_TIMEOUT_MS;
      const isWaitingExpired =
        waitingStartedAt !== null &&
        now - waitingStartedAt >= IDLE_RELOAD_TIMEOUT_MS;

      if (isIdleExpired || isWaitingExpired) {
        reloadPage();
      }
    };

    const handleUserActivity = () => {
      reloadIfExpired();
      lastInteractionAt = Date.now();
    };

    const handleFocusStateChange = () => {
      if (isPageFocused()) {
        reloadIfExpired();
        waitingStartedAt = null;
        lastInteractionAt = Date.now();
        return;
      }

      waitingStartedAt ??= Date.now();
    };

    USER_ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, {
        passive: true,
      });
    });

    document.addEventListener("visibilitychange", handleFocusStateChange);
    window.addEventListener("focus", handleFocusStateChange);
    window.addEventListener("blur", handleFocusStateChange);

    const intervalId = window.setInterval(() => {
      reloadIfExpired();
    }, IDLE_RELOAD_CHECK_INTERVAL_MS);

    return () => {
      USER_ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleUserActivity);
      });

      document.removeEventListener("visibilitychange", handleFocusStateChange);
      window.removeEventListener("focus", handleFocusStateChange);
      window.removeEventListener("blur", handleFocusStateChange);
      window.clearInterval(intervalId);
    };
  }, []);
};
